<script setup lang="ts">
import { computed, getCurrentInstance } from "vue";

import type { CalendarEvent } from "../Models/Domain";

import useUserStore from "@/Modules/User/Store";

import { dateToHoursAndMinutes } from "@/Utils/Date";
import { hasValue } from "@/Utils/Objects";
import { getDateTime } from "@/Components/Extend/XCalendar/utils";
import { useI18n } from "vue-i18n";

const userStore = useUserStore();

export interface EventCardProps {
  event: CalendarEvent;
}

const { t } = useI18n();

const props = defineProps<EventCardProps>();

const emit = defineEmits<{
  (event: "select", value: CalendarEvent): void;
}>();

const hasViewEvent = computed(() => {
  return hasValue(getCurrentInstance()?.vnode.props?.onSelect);
});

const formattedDate = computed(() => {
  return `${dateToHoursAndMinutes(getDateTime(props.event.startAt).toJSDate())} h`;
});

const selectAppointment = () => {
  emit("select", props.event);
};
</script>

<template>
  <div class="event_card" :class="{ 'event_card--hover': hasViewEvent }" @click="selectAppointment">
    <span class="event_card__time"> {{ formattedDate }}</span>

    <span class="event_card__name">
      {{ userStore.getUserIsTherapist ? props.event.patientUsername : props.event.therapistUsername }}
    </span>

    <span class="event_card__duration">
      {{ Math.floor(props.event.duration / 60) }} {{ t("calendars.units.min") }}
    </span>
  </div>
</template>

<style scoped lang="scss">
.event_card {
  display: grid;
  grid-template-columns: max-content auto max-content;
  grid-gap: $space-3;
  align-items: center;

  &--hover {
    &:hover {
      cursor: pointer;

      .event_card__time {
        @include colors.bg(content, mid);
        @include colors.color(semantic, "white");
      }
    }
  }

  &__time {
    @include colors.bg(content, low);
    @include colors.color(content, high);
    @include sp.padding(y, 1);

    border-radius: 0.25rem;
    width: $space-20;
    text-align: center;
  }

  &__name {
    @include colors.color(content, high);
    @include text.modification(ellipsis);
  }

  &__duration {
    @include colors.color(content, mid);
    @include text.general(low, m);
  }
}
</style>
